import {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useRef
} from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import ROUTES from '../config/routes';

const NavigationContext = createContext(null);

const MAIN_ROUTES = [
  ROUTES.HOME,
  ROUTES.OCTOPUSSYNC,
  ROUTES.SENTINEL,
  ROUTES.LOGIN,
  ROUTES.CONTACTS,
].map(path => (path !== '/' && path.endsWith('/') ? path.slice(0, -1) : path));

export const NavigationProvider = ({ children }) => {
  const location = useLocation();
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [userHasToggledMenu, setUserHasToggledMenu] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [isPageTransitioning, setIsPageTransitioning] = useState(false);
  const [pendingNavigationAction, setPendingNavigationAction] = useState(null);

  const prevPathnameRef = useRef(location.pathname);

  const getParentPath = path => {
    if (!path || path === '/') return '/';
    const segments = path.split('/').filter(Boolean);
    if (segments.length === 0) return '/';
    return `/${segments[0]}`;
  };

  const isMainRoute = useCallback(path => {
    const normalizedPath =
      path !== '/' && path.endsWith('/') ? path.slice(0, -1) : path;
    return MAIN_ROUTES.includes(normalizedPath);
  }, []);

  useEffect(() => {
    const currentPathnameNorm =
      location.pathname !== '/' && location.pathname.endsWith('/')
        ? location.pathname.slice(0, -1)
        : location.pathname;
    const currentParentPath = getParentPath(currentPathnameNorm);
    const previousParentPath = getParentPath(prevPathnameRef.current);

    let autoExpandedMenuTarget = null;
    if (currentParentPath === ROUTES.HOME)
      autoExpandedMenuTarget = 'Home';
    else if (currentParentPath === ROUTES.ABOUT)
      autoExpandedMenuTarget = 'About';


    if (currentParentPath !== previousParentPath) {
      setExpandedMenu(autoExpandedMenuTarget);
      setUserHasToggledMenu(false);
    } else {
      if (
        !userHasToggledMenu &&
        autoExpandedMenuTarget &&
        expandedMenu !== autoExpandedMenuTarget
      ) {
        setExpandedMenu(autoExpandedMenuTarget);
      }
    }

    prevPathnameRef.current = currentPathnameNorm;
  }, [location.pathname, userHasToggledMenu, expandedMenu]);

  const handleMenuToggle = useCallback(title => {
    setExpandedMenu(prev => {
      const newMenu = prev === title ? null : title;
      setUserHasToggledMenu(true);
      return newMenu;
    });
  }, []);

  const endPageTransition = useCallback(() => {
    setIsPageTransitioning(false);
  }, []);

  const startPageTransition = useCallback(
    (navigateActionCallback, shouldAnimate = true) => {
      if (shouldAnimate && !isPageTransitioning) {
        setIsPageTransitioning(true);
        setPendingNavigationAction(() => navigateActionCallback);
      } else if (shouldAnimate && isPageTransitioning) {
        navigateActionCallback();
      } else {
        navigateActionCallback();
      }
    },
    [isPageTransitioning]
  );
  const value = {
    expandedMenu,
    setExpandedMenu: handleMenuToggle,
    handleMenuToggle,
    userHasToggledMenu,
    activeSection,
    setActiveSection,
    isMainRoute,
    isPageTransitioning,
    endPageTransition,
    pendingNavigationAction,
    setPendingNavigationAction,
    startPageTransition
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

NavigationProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

export default NavigationContext;
